// Phase 9.2.B — Portal orders API client (2026-05-11).
//
// Typed wrappers around GET /portal/orders + GET /portal/orders/:id. The
// shapes mirror the service-layer exports in
// apps/api/src/services/portal-orders.ts — when those change, update here.
// (We don't pull from @merchd/shared because the portal types are
// portal-only; staff types live separately.)
//
// Helpers below format the raw numeric/date strings into client-display
// formats. Money is DISPLAY-only here: every total and line amount arrives
// computed by the server's shared math (the same as the client document
// /o/:uuid and the invoice) — never add prices up in the portal.
import { api } from "@/lib/api";

export type OrderStage =
  | "quote"
  | "committed"
  | "in_production"
  | "shipped"
  | "invoiced"
  | "closed"
  | "cancelled";

export type OrderState =
  | "draft"
  | "sent"
  | "viewed"
  | "approved"
  | "changes_requested"
  | "cancelled"
  | "paid";

export type ProofStatus =
  | "uploaded"
  | "awaiting_rep"
  | "awaiting_client"
  | "rep_approved"
  | "client_approved"
  | "rejected"
  | "revised";

export type ShipmentStatus =
  | "pending"
  | "in_transit"
  | "out_for_delivery"
  | "delivered"
  | "exception"
  | "returned";

export interface PortalOrderListRow {
  id: string;
  orderNumber: number;
  projectId: string;
  projectName: string;
  statusStage: OrderStage;
  statusState: OrderState;
  customerPo: string | null;
  orderDate: string;
  inHandsDate: string;
  /** The order's Total as the client document shows it (decoration charges,
   *  per-colour prices and tax included). null when pricing is hidden.
   *  Render via formatUsd(). */
  totalAmount: string | null;
  createdAt: string;
}

export interface PortalOrderListResult {
  rows: PortalOrderListRow[];
  nextCursor: { createdAt: string; id: string } | null;
  /** First page only: the years and stages this client really has orders
   *  in (never "quote"). null on "Load more" pages. */
  facets: PortalOrderFacets | null;
}

export interface PortalOrderFacets {
  /** Newest first. */
  years: number[];
  /** Pipeline order. */
  stages: OrderStage[];
  /** Every (year, stage) pair with at least one order. */
  combos: Array<{ year: number; stage: OrderStage }>;
}

export interface PortalLineVariant {
  id: string;
  color: string | null;
  size: string | null;
  material: string | null;
  skuSuffix: string | null;
  quantity: number;
  unitPrice: string | null;
  position: number;
}

/** A setup / run / other charge on a decoration — what the client pays. */
export interface PortalDecorationCharge {
  id: string;
  chargeType: string;
  amount: string;
  billingModel: "fixed" | "per_piece";
  /** How many (null = default: fixed 1, per piece the units). */
  quantity?: number | null;
  note: string | null;
}

export interface PortalLineDecoration {
  id: string;
  location: string | null;
  colorCount: number | null;
  inkOrThreadColors: string[] | null;
  method: string | null;
  size: string | null;
  color: string | null;
  /** The line's "Additional costs" holder — charges only, not a location. */
  isItemCosts: boolean;
  charges: PortalDecorationCharge[];
  position: number;
}

export interface PortalLine {
  id: string;
  lineNumber: number;
  serviceType: string;
  description: string | null;
  /** null when pricing is hidden. */
  unitPrice: string | null;
  /** The line's total from the server's shared money math — goods (per
   *  colour) + decoration charges. Never recompute it here. null when
   *  pricing is hidden. */
  amount: string | null;
  quantityTotal: number;
  decorationPath: string | null;
  productSnapshot: unknown;
  position: number;
  variants: PortalLineVariant[];
  decorations: PortalLineDecoration[];
}

export interface PortalProof {
  id: string;
  lineItemId: string | null;
  version: number;
  /** File id — pass to /portal/files/:fileId/download for a presigned URL.
   *  Phase 9.3.B made this the primary way the proof gallery renders images. */
  fileId: string;
  /** Public R2 URL when `R2_PUBLIC_URL_BASE` is configured; null otherwise.
   *  Deprecated in favor of `fileId` + the unified download endpoint. */
  fileUrl: string | null;
  originalFilename: string;
  mimeType: string;
  status: ProofStatus;
  clientVisibleAt: string;
  /** When it was approved; null unless approved. */
  approvedAt: string | null;
}

export interface PortalShipment {
  id: string;
  lineItemId: string | null;
  status: ShipmentStatus;
  carrier: string | null;
  method: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  shippedQuantity: number | null;
  projectedShipDate: string | null;
  actualShipDate: string | null;
  deliveredAt: string | null;
}

/** One of this order's invoices (only ones sent to the client or paid).
 *  balanceDue is what is still owed — straight from the server. */
export interface PortalOrderInvoice {
  id: string;
  invoiceNumber: number;
  invoiceType: "deposit" | "final";
  invoiceDate: string;
  status: "open" | "sent_to_client" | "exported" | "paid" | "voided";
  total: string;
  balanceDue: string;
  sentAt: string | null;
  paidAt: string | null;
}

export type TimelineStepKey =
  | "ordered"
  | "proof_approved"
  | "in_production"
  | "shipped"
  | "delivered"
  | "invoiced"
  | "paid";

/** One step of the order's progress, built server-side from the order's
 *  own records. `date` is null when no record holds one — never guessed. */
export interface TimelineStep {
  key: TimelineStepKey;
  label: string;
  status: "done" | "current" | "upcoming";
  date: string | null;
}

export interface PortalOrderTimeline {
  cancelled: boolean;
  steps: TimelineStep[];
}

export interface PortalOrderDetail {
  id: string;
  orderNumber: number;
  projectId: string;
  projectName: string;
  statusStage: OrderStage;
  statusState: OrderState;
  orderDate: string;
  inHandsDate: string;
  customerPo: string | null;
  terms: string;
  currency: string | null;
  introduction: string | null;
  shippingAddressSnapshot: unknown;
  hidePricing: boolean;
  /** Subtotal / tax / total exactly as the client document shows them;
   *  null when pricing is hidden. */
  totals: { subtotal: string; tax: string; total: string } | null;
  /** = totals.total. */
  totalAmount: string | null;
  createdAt: string;
  updatedAt: string | null;
  lines: PortalLine[];
  proofs: PortalProof[];
  shipments: PortalShipment[];
  invoices: PortalOrderInvoice[];
  timeline: PortalOrderTimeline;
}

// ─── Fetchers ────────────────────────────────────────────────────────

export interface ListPortalOrdersFilters {
  stage?: OrderStage[];
  year?: number;
  projectId?: string;
  limit?: number;
  cursor?: { createdAt: string; id: string } | null;
}

export async function fetchPortalOrders(
  filters: ListPortalOrdersFilters = {},
): Promise<PortalOrderListResult> {
  const params = new URLSearchParams();
  for (const s of filters.stage ?? []) params.append("stage", s);
  if (filters.year !== undefined) params.set("year", String(filters.year));
  if (filters.projectId !== undefined)
    params.set("project_id", filters.projectId);
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.cursor) {
    params.set("cursor_created_at", filters.cursor.createdAt);
    params.set("cursor_id", filters.cursor.id);
  }
  const qs = params.toString();
  return api<PortalOrderListResult>(
    `/portal/orders${qs ? `?${qs}` : ""}`,
  );
}

/** Plain words for an order's status (CEO 2026-10-08): received → artwork
 *  review → in production → shipped → invoiced / paid. */
export function orderStatusWords(stage: OrderStage, state: OrderState): string {
  if ((stage === "invoiced" || stage === "closed") && state === "paid") return "Paid";
  return STAGE_LABEL[stage];
}

export interface PortalPendingWebsiteOrder {
  id: string;
  reference: string;
  statusLabel: string;
  total: string | null;
  paidAt: string | null;
  items: Array<{ name: string; quantity: number }>;
}

/** Paid website orders still being set up (no sales order yet). */
export async function fetchPendingWebsiteOrders(): Promise<{ orders: PortalPendingWebsiteOrder[] }> {
  return api<{ orders: PortalPendingWebsiteOrder[] }>("/portal/orders/website-pending");
}

export async function fetchPortalOrder(
  id: string,
): Promise<PortalOrderDetail> {
  return api<PortalOrderDetail>(`/portal/orders/${id}`);
}

// Phase 9.5 — Re-order. POST clones the source order's lines into a
// new draft. Returns the new order id (frontend toasts + may navigate
// back to /orders so the rep can pick it up; client doesn't see the
// draft).
export interface ReorderResponse {
  new_order_id: string;
  new_order_number: number;
}

export type ReorderUnpayableReason =
  | "stage_not_reorderable"
  | "cancelled";

export interface ReorderLineEditInput {
  line_item_id: string;
  quantity: number;
}

/** Slice E (2026-08-30): the client reviews and edits before submitting —
 *  included lines with their (possibly changed) quantities, plus an
 *  optional note that rides to the rep. */
export async function reorderPortalOrder(
  id: string,
  edits: { lines: ReorderLineEditInput[]; note?: string },
): Promise<ReorderResponse> {
  return api<ReorderResponse>(`/portal/orders/${id}/reorder`, {
    method: "POST",
    body: JSON.stringify(edits),
  });
}

const REORDERABLE_STAGES: OrderStage[] = ["shipped", "invoiced", "closed"];

/** True when the order's stage permits client-initiated re-order. */
export function isReorderableStage(stage: OrderStage): boolean {
  return REORDERABLE_STAGES.includes(stage);
}

// ─── Display helpers ─────────────────────────────────────────────────

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export function formatUsd(amountString: string | null | undefined): string {
  if (amountString == null) return "—";
  const n = Number.parseFloat(amountString);
  if (!Number.isFinite(n)) return "—";
  return usdFormatter.format(n);
}

/** "2026-05-11" or ISO → "May 11, 2026". Empty/null → "—". */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  // A bare "YYYY-MM-DD" (in-hands date) is a calendar day, not an instant:
  // new Date() reads it as UTC midnight — the day BEFORE anywhere west of
  // London. Read it as local noon instead.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** A line's title — the client document's rule (/o/:uuid lineName): the
 *  product's name, else the first line of the description, else "Item N". */
export function lineName(l: PortalLine): string {
  const name = (l.productSnapshot as { name?: unknown } | null)?.name;
  return (
    (typeof name === "string" ? name.trim() : "") ||
    l.description?.split("\n")[0]?.trim() ||
    `Item ${l.lineNumber}`
  );
}

/** The line's first product photo, as the client document picks it. */
export function lineImage(l: PortalLine): string | null {
  const urls = (l.productSnapshot as { imageUrls?: unknown } | null)?.imageUrls;
  if (!urls || typeof urls !== "object") return null;
  const list = Array.isArray(urls) ? urls : Object.values(urls);
  return (list.find((u) => typeof u === "string" && u) as string | undefined) ?? null;
}

/** A charge's label, as the client document writes it. */
export function chargeLabel(c: PortalDecorationCharge): string {
  if (c.note && c.note.trim()) return c.note.trim();
  const map: Record<string, string> = {
    setup: "Setup",
    run: "Run charge",
    digitizing: "Digitizing",
    rush: "Rush",
    screen: "Screen charge",
    pms_match: "PMS match",
  };
  return map[c.chargeType] ?? "Additional charge";
}

/** Price each for the goods: one price, or "from" when colours/sizes differ
 *  (the client document's label). Display only — not a total. */
export function unitPriceLabel(l: PortalLine): string | null {
  if (l.unitPrice == null) return null;
  const prices = l.variants
    .filter((v) => v.quantity > 0)
    .map((v) => Number(v.unitPrice ?? l.unitPrice));
  if (prices.length === 0) return formatUsd(l.unitPrice);
  const lo = Math.min(...prices);
  const hi = Math.max(...prices);
  return lo === hi ? formatUsd(String(lo)) : `from ${formatUsd(String(lo))}`;
}

/** Plain words for an invoice's kind (the Invoices pages' wording). */
export const INVOICE_TYPE_LABEL: Record<PortalOrderInvoice["invoiceType"], string> = {
  // Every invoice is just an invoice; prepay is its terms (Stephanie 2026-10-27).
  deposit: "Prepay",
  final: "Invoice",
};

/** Human-friendly stage label. */
export const STAGE_LABEL: Record<OrderStage, string> = {
  quote: "Quote",
  committed: "Received · artwork review",
  in_production: "In production",
  shipped: "Shipped",
  invoiced: "Invoiced",
  closed: "Complete",
  cancelled: "Cancelled",
};

/** Tailwind class set for the stage pill — palette stays brand-locked. */
export const STAGE_PILL_CLASS: Record<OrderStage, string> = {
  quote: "bg-cream-100 text-brown-500",
  committed: "bg-peach-100 text-brown-500",
  in_production: "bg-peach-200 text-brown-500",
  shipped: "bg-ok-bg text-ok",
  invoiced: "bg-ok-bg text-ok",
  closed: "bg-cream-200 text-brown-400",
  cancelled: "bg-cream-50 text-brown-300",
};

export const SHIPMENT_STATUS_LABEL: Record<ShipmentStatus, string> = {
  pending: "Pending",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  exception: "Exception",
  returned: "Returned",
};

export const PROOF_STATUS_LABEL: Record<ProofStatus, string> = {
  uploaded: "Uploaded",
  awaiting_rep: "In rep review",
  awaiting_client: "Awaiting your review",
  rep_approved: "Rep approved",
  client_approved: "Approved",
  rejected: "Changes requested",
  revised: "Revised",
};
